<script setup lang="ts">
import CardHeading from "./CardHeading.vue";
// Vue
import { computed, onMounted, watch } from "vue";

// Types
import { EmissionFactorType, WizardProjectType } from "@/features/BuildingProject";

// Data Providers
import { useEmissionFactorSources } from "@/dataProviders/EmissionFactorSources";
import { useProjectRevisionsStore } from "@/pages/ProjectVersionHistory/stores/projectRevisions.store";
import type { ProjectRevisionEmissionFactor } from "@/pages/ProjectVersionHistory/types/projectRevision.types";

// GraphQL
import { Emission_Factor_Type_Enum } from "@/graphql";

// Features
import EmissionConsumptionChoice from "@/features/EmissionConsumptionChoice";

// Components
import { BadgeCheck, Flame, PlugZap, Zap } from "@lucide/vue";
import DateInput from "@/components/Reusables/Inputs/DateInput.vue";
import NumberInput from "@/components/Reusables/Inputs/NumberInput.vue";
import TextInputNew from "@/components/Reusables/Inputs/TextInputNew.vue";
import B6EnergyFrameGuidance from "@/features/BuildingProject/components/WizardFlow/B6EnergyFrameGuidance.vue";
import StepIntro from "./StepIntro/StepIntro.vue";
import { useWizardProject } from "@/features/BuildingProject/composables/useWizardProject";

const props = defineProps<{
  wasValidated: boolean;
  project: WizardProjectType;
}>();

const emissionFactorSources = useEmissionFactorSources();
const revisionsStore = useProjectRevisionsStore();

const { project, projectId, lens } = useWizardProject(() => props.project);

/** B6's two supply rows share one object, the same shape A5 uses. */
const operationalEnergy = lens(
  (current) => current.emissionCalculationAssumptions.projectEnergyData.operational,
  (current, value) => (current.emissionCalculationAssumptions.projectEnergyData.operational = value)
);

/** A row with nothing in it, for a project that has never had one. */
const emptyEmissionFactor = (): EmissionFactorType => ({ sourceId: null, type: "", value: null });

// B6 lives in Hasura's project_operational, which the API hard-deletes and re-inserts on every
// save, and energy changes reach the trail only as a summary line — so
// neither the table nor the trail can say what the heat supply was at an older save point. The
// revision's own project_operational row can, via the projectRevision.operational relationship, exactly as the
// dashboard address reads project_revision.location.
//
// Applied only while PREVIEWING, not for every pinned read the way readLocationFor is. This is a
// form, so its rule is "what you see is what Save writes": the overlay, the read-only state and the
// disabled Save button are gated on the same condition. Preview is the explicit, reversible,
// read-only mode; when an admin has merely parked the current pointer on an older save point the
// team keeps editing the live head on the settings page, and showing a fold there would invite writing
// yesterday's values back over today's.
//
// The overlay never touches the project wrapper — it stays display-only local state, so even if
// something slipped past the Save gate there is nothing folded in the object Save serialises.

const revisionOperational = computed(() =>
  projectId.value && revisionsStore.isPreviewing(projectId.value) ? revisionsStore.readOperationalFor(projectId.value) : null
);

/** True while the step is showing a revision's saved values instead of the live, editable ones. */
const isReadOnly = computed(() => revisionOperational.value !== null);

/** Snapshot emission factor (the API's project-document shape) → the model the wizard binds to. */
const toEmissionFactor = (factor: ProjectRevisionEmissionFactor | null | undefined): EmissionFactorType => ({
  sourceId: factor?.sourceId ?? null,
  customSourceId: factor?.customSourceId ?? undefined,
  // Built-in sources carry the description on the factor row, custom ones on the source row —
  // mirrors ProjectEmissionFactor.ResolvedDescription in realtimelca-api.
  description: factor?.description ?? factor?.customEmissionFactor?.description ?? undefined,
  type: factor?.type ?? "",
  value: factor?.value ?? null,
});

/**
 * The two supply rows, read from and written to the stored project.
 *
 * They used to sit on refs seeded once from a destructure at setup, with a pair of deep watchers copying them
 * back into the project afterwards. Two consequences: anything that changed the stored rows while this step was
 * mounted was invisible here, and a later edit would have pushed the stale copy back over it.
 *
 * The getter hands out the stored row itself, so the deep edits the choice component makes in place land where
 * they belong, and the setter handles the case where it emits a whole new row.
 */
const heatSupply = computed<EmissionFactorType>({
  get: () =>
    revisionOperational.value
      ? toEmissionFactor(revisionOperational.value.heatSupply)
      : operationalEnergy.value?.heat ?? emptyEmissionFactor(),
  set: (value) => {
    if (isReadOnly.value) return;
    operationalEnergy.value = { ...operationalEnergy.value, heat: value };
  },
});

const electricitySupply = computed<EmissionFactorType>({
  get: () =>
    revisionOperational.value
      ? toEmissionFactor(revisionOperational.value.electricitySupply)
      : operationalEnergy.value?.electricity ?? emptyEmissionFactor(),
  set: (value) => {
    if (isReadOnly.value) return;
    operationalEnergy.value = { ...operationalEnergy.value, electricity: value };
  },
});

const isDanish = computed(() => project.value.buildingAddress?.country === "Denmark");

const yearOfCommissioning = computed(
  () => project.value.emissionCalculationAssumptions?.emissionMetaData?.yearOfCommissioning ?? 0
);

const yearOfConstruction = computed(
  () => project.value.emissionCalculationAssumptions?.emissionMetaData?.yearOfConstruction ?? 0
);

const considerationPeriod = computed(
  () => project.value.emissionCalculationAssumptions?.calculationType?.considerationPeriod ?? 0
);

const totalHeatedFloorArea = computed<number | null>(() => {
  const heatArea = project.value.emissionCalculationAssumptions?.heatArea;
  const above = heatArea?.heatedFloorAreaAboveGround ?? 0;
  const basement = heatArea?.heatedBasementArea ?? 0;
  const total = (above as number) + (basement as number);
  return total > 0 ? total : null;
});

const exportedElectricity = computed<number | null>({
  get() {
    // The snapshot stores exported electricity as a full emission factor; the wizard binds a number.
    if (revisionOperational.value) return revisionOperational.value.exportedElectricity?.value ?? null;
    const value = project.value.emissionCalculationAssumptions.projectEnergyData.operationExportedElectricity;
    return value === 0 ? null : value;
  },
  set(val) {
    if (isReadOnly.value) return;
    project.value.emissionCalculationAssumptions.projectEnergyData.operationExportedElectricity = val === 0 ? null : val;
  },
});

const validatedBy = computed<string>({
  get: () =>
    revisionOperational.value
      ? revisionOperational.value.validatedBy ?? ""
      : project.value.emissionCalculationAssumptions.projectEnergyData.validatedBy,
  set: (val) => {
    if (isReadOnly.value) return;
    project.value.emissionCalculationAssumptions.projectEnergyData.validatedBy = val;
  },
});

const validationDate = computed<string | null>({
  get: () =>
    revisionOperational.value
      ? revisionOperational.value.validatedAt ?? null
      : project.value.emissionCalculationAssumptions.projectEnergyData.validationDate,
  set: (val) => {
    if (isReadOnly.value) return;
    project.value.emissionCalculationAssumptions.projectEnergyData.validationDate = val;
  },
});

const isValidationRequired = computed(() => {
  // Reads the DISPLAYED supplies so the required markers describe what is on screen — while
  // previewing that is the revision's own state, not the live one.
  const heat = heatSupply.value;
  const electricity = electricitySupply.value;
  const exported = exportedElectricity.value;

  const hasHeatInput = heat?.sourceId && typeof heat?.value === "number" && !isNaN(heat.value);
  const hasElectricityInput = electricity?.sourceId && typeof electricity?.value === "number" && !isNaN(electricity.value);
  const hasExportedInput = typeof exported === "number" && exported > 0;

  return !!hasHeatInput || !!hasElectricityInput || !!hasExportedInput;
});

/**
 * Materialised for payload parity, not for correctness.
 *
 * The watchers this replaces carried `immediate: true`, so the empty rows were already written into the
 * project the moment this step appeared, and create has always sent them. Nothing needs them: the choice
 * component emits a whole new row every time and never edits one in place. Dropping this would quietly change
 * what create posts, which is not this commit's business.
 *
 * Installation does not do this, and should not: its watchers had no `immediate`, so its untouched rows were
 * never written, and materialising them there would be the change this avoids here.
 */
onMounted(() => {
  if (isReadOnly.value) return;
  if (operationalEnergy.value?.heat && operationalEnergy.value?.electricity) return;

  operationalEnergy.value = {
    ...operationalEnergy.value,
    heat: operationalEnergy.value?.heat ?? emptyEmissionFactor(),
    electricity: operationalEnergy.value?.electricity ?? emptyEmissionFactor(),
  };
});

watch(
  () => project.value.emissionCalculationAssumptions.projectEnergyData.validationDate,
  (val) => {
    if (val === "") {
      project.value.emissionCalculationAssumptions.projectEnergyData.validationDate = null;
    }
  }
);

watch(
  () => project.value.emissionCalculationAssumptions.projectEnergyData.validatedBy,
  (val) => {
    if (val === "") {
      project.value.emissionCalculationAssumptions.projectEnergyData.validatedBy = "";
    }
  }
);
</script>

<template>
  <div class="w-full">
    <StepIntro
      class="mb-6"
      heading="How much energy does the building use?"
      hint="B6, operational energy use. The figures come from the building's energy frame calculation." />
    <!-- A native disabled fieldset makes every control below inert while a revision's saved values
         are on screen — including the combobox, whose trigger is a real <button>. Nothing to thread
         a `disabled` prop through, and it stays keyboard-correct. -->
    <fieldset
      class="flex w-full flex-col gap-3"
      :disabled="isReadOnly">
      <!-- BR18 Energy Frame Guidance (Danish projects only) -->
      <B6EnergyFrameGuidance
        v-if="isDanish"
        :heated-floor-area="totalHeatedFloorArea"
        :building-type-enum="project.buildingTypeEnum ?? null" />

      <div class="rounded-8 bg-slate-0 p-px-20 flex flex-col gap-3 border border-slate-300">
        <div
          class="-mx-px-20 -mt-px-20 px-px-20 pt-px-20 rounded-t-8 flex flex-col gap-1 border-b border-slate-300 bg-slate-50 pb-3">
          <div class="flex items-center gap-2">
            <span class="bg-app-blue-50 rounded-4 flex shrink-0 items-center justify-center p-1">
              <Flame
                class="icon-sm text-app-blue-600"
                :stroke-width="1.5"
                aria-hidden="true" />
            </span>
            <CardHeading class="t-sh4 text-type-default">Heat supply</CardHeading>
          </div>
        </div>
        <div class="grid grid-cols-12 gap-4">
          <EmissionConsumptionChoice
            v-model="heatSupply"
            :was-validated="props.wasValidated"
            :emission-factor-sources="emissionFactorSources.getHeatSupplySources()"
            :custom-emission-factor-sources="emissionFactorSources.getHeatSupplyCustomSources()"
            :emission-factor-types="[
              Emission_Factor_Type_Enum.HEATING,
              Emission_Factor_Type_Enum.DISTRICT_HEATING,
              Emission_Factor_Type_Enum.LINE_GAS,
              Emission_Factor_Type_Enum.ELECTRICITY,
            ]"
            :year-of-commissioning="yearOfCommissioning"
            :year-of-construction="yearOfConstruction"
            :consideration-period="considerationPeriod"
            :country="project.buildingAddress?.country"
            variant="operational" />
        </div>
      </div>

      <div class="rounded-8 bg-slate-0 p-px-20 flex flex-col gap-3 border border-slate-300">
        <div
          class="-mx-px-20 -mt-px-20 px-px-20 pt-px-20 rounded-t-8 flex flex-col gap-1 border-b border-slate-300 bg-slate-50 pb-3">
          <div class="flex items-center gap-2">
            <span class="bg-app-blue-50 rounded-4 flex shrink-0 items-center justify-center p-1">
              <Zap
                class="icon-sm text-app-blue-600"
                :stroke-width="1.5"
                aria-hidden="true" />
            </span>
            <CardHeading class="t-sh4 text-type-default">Electricity supply</CardHeading>
          </div>
        </div>
        <div class="grid grid-cols-12 gap-4">
          <EmissionConsumptionChoice
            v-model="electricitySupply"
            :was-validated="props.wasValidated"
            :emission-factor-sources="emissionFactorSources.getElectricitySupplySources()"
            :custom-emission-factor-sources="emissionFactorSources.getElectricitySupplyCustomSources()"
            :emission-factor-types="[Emission_Factor_Type_Enum.ELECTRICITY]"
            :year-of-commissioning="yearOfCommissioning"
            :year-of-construction="yearOfConstruction"
            :consideration-period="considerationPeriod"
            :country="project.buildingAddress?.country"
            variant="operational" />
        </div>
      </div>

      <div class="rounded-8 bg-slate-0 p-px-20 @container flex flex-col gap-3 border border-slate-300">
        <div
          class="-mx-px-20 -mt-px-20 px-px-20 pt-px-20 rounded-t-8 flex flex-col gap-1 border-b border-slate-300 bg-slate-50 pb-3">
          <div class="flex items-center gap-2">
            <span class="bg-app-blue-50 rounded-4 flex shrink-0 items-center justify-center p-1">
              <PlugZap
                class="icon-sm text-app-blue-600"
                :stroke-width="1.5"
                aria-hidden="true" />
            </span>
            <CardHeading class="t-sh4 text-type-default">Exported electricity</CardHeading>
          </div>
          <p class="t-caption text-type-muted">Electricity the building exports to the grid, from solar panels and similar.</p>
        </div>
        <div class="grid gap-4 @md:grid-cols-2 @lg:grid-cols-3">
          <NumberInput
            v-model:value="exportedElectricity"
            label="Exported electricity (kWh/m&#178; year)"
            color="primary"
            :tooltip="project.buildingAddress.country === 'Denmark' ? 'Max is 25 for projects in Denmark' : ''"
            :max="project.buildingAddress.country === 'Denmark' ? 25 : undefined"
            :positive="true"
            data-cy="exported-electricity"
            :align-right="true" />
        </div>
      </div>

      <div class="rounded-8 bg-slate-0 p-px-20 @container flex flex-col gap-3 border border-slate-300">
        <div
          class="-mx-px-20 -mt-px-20 px-px-20 pt-px-20 rounded-t-8 flex flex-col gap-1 border-b border-slate-300 bg-slate-50 pb-3">
          <div class="flex items-center gap-2">
            <span class="bg-app-blue-50 rounded-4 flex shrink-0 items-center justify-center p-1">
              <BadgeCheck
                class="icon-sm text-app-blue-600"
                :stroke-width="1.5"
                aria-hidden="true" />
            </span>
            <CardHeading class="t-sh4 text-type-default">Validation</CardHeading>
          </div>
          <p class="t-caption text-type-muted">Who checked the energy figures, and when. Required once any energy is entered.</p>
        </div>
        <div class="grid gap-4 @md:grid-cols-2 @lg:grid-cols-3">
          <TextInputNew
            v-model="validatedBy"
            label="Energy numbers validated by"
            :text-only="true"
            data-cy="energy-numbers-validated-by"
            :error-msg="
              wasValidated && isValidationRequired && !validatedBy
                ? 'Please enter the name/initials of who validated the energy numbers'
                : undefined
            "
            :remove-leading-whitespace="true"
            :required="isValidationRequired" />

          <DateInput
            v-model:value="validationDate"
            color="primary"
            label="Date of energy validation"
            :error-msg="
              wasValidated && isValidationRequired && !validationDate
                ? 'Please enter a date for validation of energy numbers'
                : undefined
            "
            data-cy="date-of-validation"
            :required="isValidationRequired" />
        </div>
      </div>
    </fieldset>
  </div>
</template>
